import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import TablePagination from "@mui/material/TablePagination";

import "./DeviceParameterReports.css";

const API_URL = process.env.REACT_APP_API_DJANGO_URL;

export default function DeviceParameterReports() {
  const token = localStorage.getItem("accessToken");
  const customerId = localStorage.getItem("customer_id");
  const role = localStorage.getItem("role");
  const userId = localStorage.getItem("userId");
  const customerName = localStorage.getItem("customer_name");

  const [sites, setSites] = useState([]);
  const [devices, setDevices] = useState([]);

  const [selectedSite, setSelectedSite] = useState("");
  const [selectedDevice, setSelectedDevice] = useState("");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState("");

  const [groupedTelemetry, setGroupedTelemetry] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(false);

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    data: {},
  };

  const formatParamValue = (param) => {
    const key = param.key_name;
    const val = param.value;

    if (val === null || val === undefined) return "-";

    if (key.includes("Pr")) {
      return `${(val / 100).toFixed(2)} Kg/cm²`;
    }

    if (key.includes("Mod")) {
      return val === 1 ? "AUTO" : "MANUAL";
    }

    if (key.includes("Sts")) {
      return val === 1 ? "ON" : "OFF";
    }

    return val;
  };




  useEffect(() => {
    const fetchCustomers = async () => {


      // ✅ Customer Admin
      if (role === "CA" || role === "SE" || role === "DC") {
        setSelectedCustomer(customerId);

        // Used for PDF export
        setCustomers([
          {
            id: customerId,
            name: customerName || "Customer",
          },
        ]);

        return;
      }



      try {
        const res = await axios.get(
          `${API_URL}/api/get_customers/`,
          config
        );

        const customerList = (res.data || []).map((c) => ({
          id: c.tb_customer_id,
          name: c.customer_name,
        }));

        setCustomers(customerList);

        if (customerList.length > 0) {
          setSelectedCustomer(customerList[0].id);
        }
      } catch (err) {
        console.error("Error fetching customers:", err);
      }
    };

    fetchCustomers();
  }, [role, customerId]);


  const fetchSites = async () => {
    try {
      let siteList = [];

      // ✅ Supervisor
      if (role === "S") {
        const response = await axios.get(
          `${API_URL}/api/supervisor_sites/${userId}/`,
          config
        );

        siteList = (response.data.sites || []).map((site) => ({
          asset_id: site.asset_id,
          name: site.name,
        }));
      }

      // ✅ Site Engineer
      else if (role === "SE") {
        const response = await axios.get(
          `${API_URL}/api/engineer_sites/${userId}/`,
          config
        );

        siteList = (response.data.sites || []).map((site) => ({
          asset_id: site.asset_id,
          name: site.site_name,
        }));
      }

      // ✅ Super Admin / Customer
      else {
        const response = await axios.get(
          `${API_URL}/api/get_customer_sites/${selectedCustomer}/`,
          config
        );

        siteList = response.data.data || [];
      }

      setSites(siteList);

      if (siteList.length > 0) {
        setSelectedSite(siteList[0].asset_id);
        setPage(0);
      }
    } catch (err) {
      console.error("Error fetching sites:", err);
    }
  };


  useEffect(() => {
    if (selectedCustomer || userId) {
      fetchSites();
    }
  }, [selectedCustomer, role, userId]);



  useEffect(() => {
    if (selectedSite) {
      fetchDevices();
    }
  }, [selectedSite]);

  const fetchDevices = async () => {
    try {
      const res = await axios.get(
        `${API_URL}/api/site_devices/${selectedSite}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const deviceList = res.data.devices || [];

      setDevices(deviceList);

      if (deviceList.length > 0) {
        setSelectedDevice(deviceList[0].device_id);
      } if (deviceList.length > 0) {
        setSelectedDevice(deviceList[0].device_id);
        setPage(0);
      } else {
        setSelectedDevice("");
        setGroupedTelemetry([]);
      }

    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (selectedDevice) {
      fetchGroupedTelemetry();
    }
  }, [selectedDevice]);

  const fetchGroupedTelemetry = async () => {
    setLoading(true);

    try {
      const res = await axios.get(
        `${API_URL}/api/get_grouped_telemetry/${selectedDevice}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setGroupedTelemetry(res.data.groups || []);
      setPage(0);
    } catch (err) {
      console.error(err);
      setGroupedTelemetry([]);
    }

    setLoading(false);
  };


  const selectedSiteName =
    sites.find((s) => s.asset_id === selectedSite)?.name || "";

  const selectedDeviceName =
    devices.find((d) => d.device_id === selectedDevice)?.device_name || "";

  const paginatedGroups = groupedTelemetry.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );



  const exportCSV = () => {
    const rows = [];

    groupedTelemetry.forEach((group) => {
      group.parameters.forEach((param, index) => {
        rows.push([
          index === 0 ? group.group_name : "",
          param.display_name,
          formatParamValue(param),
        ]);
      });
    });
    // const customerNameForExport =
    //   role === "CA" || role === "SE"
    //     ? localStorage.getItem("customer_name") || "Customer"
    //     : customers.find((c) => c.id === selectedCustomer)?.name || "Customer";


    const customerNameForExport =
      customers.find((c) => c.id === selectedCustomer)?.name || "Customer";

    const csvContent = [
      // [`Customer Name:,${customerNameForExport}`],
      ...(role !== "CA" && role !== "SE" && role !== "DC"
        ? [[`Customer Name:,${customerNameForExport}`]]
        : []),
      ["Site Name", selectedSiteName],
      ["Device Name", selectedDeviceName],
      [],
      ["Group Name", "Parameter Name", "Value"],
      ...rows,
    ]
      .map((e) => e.join(","))
      .join("\n");

    const csv = "data:text/csv;charset=utf-8," + csvContent;

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `${selectedDeviceName}_Parameter_Report.csv`;
    link.click();
  };




  const exportPDF = () => {
    const doc = new jsPDF();
    //  const customerName =
    // role === "CA" || role === "SE"
    //   ? localStorage.getItem("customer_name") || "Customer"
    //   : customers.find((c) => c.id === selectedCustomer)?.name || "Customer";


    const customerName =
      customers.find((c) => c.id === selectedCustomer)?.name || "Customer";

    doc.setFontSize(16);
    doc.text("Device Parameter Report", 14, 12);

    doc.setFontSize(11);
    //  doc.text(`Customer Name: ${customerName}`, 14, 18);
    // doc.text(`Site Name : ${selectedSiteName}`, 14, 26);
    // doc.text(`Device Name : ${selectedDeviceName}`, 14, 34);


    let y = 18;

    if (role !== "CA" && role !== "SE" && role !== "DC") {
      doc.text(`Customer Name: ${customerName}`, 14, y);
      y += 8;
    }

    doc.text(`Site Name : ${selectedSiteName}`, 14, y);
    y += 8;

    doc.text(`Device Name : ${selectedDeviceName}`, 14, y);

    const body = [];

    groupedTelemetry.forEach((group) => {
      group.parameters.forEach((param, index) => {
        body.push([
          index === 0 ? group.group_name : "",
          param.display_name,
          formatParamValue(param),
        ]);
      });
    });

    autoTable(doc, {
      startY: y + 8,
      head: [["Group Name", "Parameter", "Value"]],
      body,
    });

    doc.save(`${customerName}_${selectedDeviceName}_Parameter_Report.pdf`);
  };



  return (
    <Box className="report-container">

      <Box className="top-bar">
        {role !== "CA" && role !== "SE" && role !== "DC" && (
          <FormControl className="dropdown">
            <InputLabel>Customer</InputLabel>

            <Select
              value={selectedCustomer}
              label="Customer"
              onChange={(e) => setSelectedCustomer(e.target.value)}
            >
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <FormControl className="dropdown">


          <InputLabel>Site</InputLabel>

          <Select
            value={selectedSite}
            label="Site"
            onChange={(e) => setSelectedSite(e.target.value)}
          >
            {sites.map((site) => (
              <MenuItem
                key={site.asset_id}
                value={site.asset_id}
              >
                {site.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl className="dropdown">
          <InputLabel>Device</InputLabel>

          <Select
            value={selectedDevice}
            label="Device"
            onChange={(e) => setSelectedDevice(e.target.value)}
          >
            {devices.map((device) => (
              <MenuItem
                key={device.device_id}
                value={device.device_id}
              >
                {device.device_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box className="export-buttons">
          <Button
            className="btn-csv"
            onClick={exportCSV}
          >
            CSV
          </Button>

          <Button
            className="btn-pdf"
            onClick={exportPDF}
          >
            PDF
          </Button>
        </Box>

      </Box>

      <TableContainer component={Paper} className="table">
        <Table>

          <TableHead>
            <TableRow>
              <TableCell>Group Name</TableCell>
              <TableCell>Parameter Name</TableCell>
              <TableCell>Value</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : groupedTelemetry.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No Data Found
                </TableCell>
              </TableRow>
            ) : (
              paginatedGroups.map((group) =>
                group.parameters.map((param, index) => (
                  <TableRow key={`${group.group_name}-${index}`}>
                    {index === 0 && (
                      <TableCell
                        rowSpan={group.parameters.length}
                        className="group-cell"
                      >
                        {group.group_name}
                      </TableCell>
                    )}

                    <TableCell>{param.display_name}</TableCell>

                    <TableCell>
                      {formatParamValue(param)}
                    </TableCell>
                  </TableRow>
                ))
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={groupedTelemetry.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, newPage) => setPage(newPage)}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(parseInt(event.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />

    </Box>
  );
}